/**
 * Pictogrammes SVG inline du site (traits simples, couleur = currentColor).
 * Décoratifs : aria-hidden, le sens est toujours porté par le texte voisin.
 */
import React from 'react';

const base = { width: 24, height: 24, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': 'true', focusable: 'false' };

const PATHS = {
  shield: <><path d="M12 3l7 3v5c0 5-3.5 8.5-7 10-3.5-1.5-7-5-7-10V6l7-3z" /><path d="M9.5 12l2 2 3.5-4" /></>,
  eye: <><path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6z" /><circle cx="12" cy="12" r="3" /></>,
  pin: <><path d="M12 21s-6-5.5-6-11a6 6 0 0112 0c0 5.5-6 11-6 11z" /><circle cx="12" cy="10" r="2.2" /></>,
  hands: <><path d="M4 12.5l3-3.5 4 1.5 3-1.5 3 2.5" /><path d="M4 12.5V18l4 2h5l6-4.5-1.5-2-4 1.5" /><path d="M11 10.5l-2 2.5 3 1.5" /></>,
  bell: <><path d="M6 16V11a6 6 0 0112 0v5l1.5 2h-15L6 16z" /><path d="M10 20a2 2 0 004 0" /></>,
  check: <><circle cx="12" cy="12" r="9" /><path d="M8 12.5l2.5 2.5L16 9.5" /></>,
  network: <><circle cx="12" cy="6" r="2.2" /><circle cx="5" cy="17" r="2.2" /><circle cx="19" cy="17" r="2.2" /><path d="M10.8 7.8L6.4 15M13.2 7.8l4.4 7.2M7.2 17h9.6" /></>,
  chart: <><path d="M4 20h16" /><path d="M7 16v-5M12 16V8M17 16v-3" /></>,
  puzzle: <><path d="M10 4h4v2.5a1.5 1.5 0 003 0V4h3v4h-2.5a1.5 1.5 0 000 3H20v4h-3v2.5a1.5 1.5 0 01-3 0V15h-4v-2.5a1.5 1.5 0 00-3 0V15H4v-4h2.5a1.5 1.5 0 000-3H4V4h6z" /></>,
  sliders: <><path d="M4 7h10M18 7h2M4 12h3M11 12h9M4 17h12M20 17h0" /><circle cx="16" cy="7" r="2" /><circle cx="9" cy="12" r="2" /><circle cx="18" cy="17" r="2" /></>,
  minimize: <><path d="M9 3H3v6M15 3h6v6M3 15v6h6M21 15v6h-6" /><rect x="9" y="9" width="6" height="6" rx="1" /></>,
  lock: <><rect x="5" y="10" width="14" height="10" rx="2" /><path d="M8 10V7a4 4 0 018 0v3" /><circle cx="12" cy="15" r="1.2" /></>,
  alert: <><path d="M12 3l9.5 16.5h-19L12 3z" /><path d="M12 10v4M12 17.2v.3" /></>,
  car: <><path d="M4 14l1.5-5A2 2 0 017.4 7.5h9.2a2 2 0 011.9 1.5L20 14v5h-2.5v-2h-11v2H4v-5z" /><circle cx="7.5" cy="14.5" r="1" /><circle cx="16.5" cy="14.5" r="1" /></>,
  health: <><path d="M12 21s-7.5-4.5-7.5-10A4 4 0 0112 8a4 4 0 017.5 3c0 5.5-7.5 10-7.5 10z" /><path d="M12 10v5M9.5 12.5h5" /></>,
  phone: <><rect x="7" y="3" width="10" height="18" rx="2" /><path d="M11 17.5h2" /></>,
  camera: <><path d="M4 8h3l1.5-2h7L17 8h3v11H4V8z" /><circle cx="12" cy="13" r="3" /></>,
  building: <><path d="M4 21V8l8-4 8 4v13" /><path d="M9 21v-5h6v5M9 11h.01M15 11h.01M12 11h.01" /></>,
  mail: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></>,
  arrow: <><path d="M5 12h14M13 6l6 6-6 6" /></>,
  chevron: <><path d="M6 9l6 6 6-6" /></>,
  close: <><path d="M6 6l12 12M18 6L6 18" /></>,
  menu: <><path d="M4 7h16M4 12h16M4 17h16" /></>,
  radar: <><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="4" /><path d="M12 12l5.5-5.5" /></>,
  clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
};

export function Icon({ name, size = 24, className, ...rest }) {
  const d = PATHS[name] || PATHS.check;
  return (
    <svg {...base} width={size} height={size} className={className} {...rest}>{d}</svg>
  );
}

/**
 * Marque CIVIK — bouclier au « C » et point vert, reprise de l'icône de
 * l'application (assets/icon.png de civik-app). Même dessin, même vert.
 */
export function LogoMark({ size = 36, className, title }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" className={className} role={title ? 'img' : undefined} aria-hidden={title ? undefined : 'true'} focusable="false">
      {title ? <title>{title}</title> : null}
      <path d="M14 8h36l8 8v20L32 60 6 36V16l8-8z" fill="none" stroke="currentColor" strokeWidth="6" strokeLinejoin="round" />
      <path d="M41.5 22.5a12.5 12.5 0 100 19" fill="none" stroke="currentColor" strokeWidth="7" strokeLinecap="round" />
      <circle cx="45" cy="42" r="5" fill="#22A559" />
    </svg>
  );
}

/** Wordmark « CIVIK » : le K rouge est le repère historique du site (conservé). */
export function Wordmark({ className }) {
  return <span className={className}>CIVI<span className="lp-logo-k">K</span></span>;
}
